import { useQuery } from "@apollo/client";
import { Alert, Box, LinearProgress, Stack, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { type BulkSendProgress, PROGRESS_POLL_MS, SEND_PROGRESS } from "./helpers";

/**
 * Follows a queued bulk run.
 *
 * Sending no longer finishes inside the request that starts it — it is paced
 * across batches in the worker, which can take minutes for a large list. So the
 * screen stops pretending to wait for a result and instead shows the run
 * happening, reading the counts back from the server every couple of seconds.
 */
const BulkSendProgressPanel: React.FC<{
  sendId: string;
  verb?: string;
  onComplete?: () => void;
}> = ({ sendId, verb = "Sent", onComplete }) => {
  const { data } = useQuery(SEND_PROGRESS, {
    variables: { sendId },
    pollInterval: PROGRESS_POLL_MS,
    fetchPolicy: "network-only",
  });

  const progress: BulkSendProgress | undefined = data?.chatAutomationsSendProgress;
  const reported = useRef<string | null>(null);
  const finished = progress?.status === "done" || progress?.status === "failed";
  useEffect(() => {
    if (finished && reported.current !== sendId) {
      reported.current = sendId;
      onComplete?.();
    }
  }, [finished, sendId, onComplete]);
  if (!progress) {
    return (
      <Alert severity="info">
        Queued — this runs in the background, so you can leave this page open or come back to it.
      </Alert>
    );
  }

  const done = progress.sent + progress.failed;
  const percent = progress.total > 0 ? Math.round((done * 100) / progress.total) : 0;

  return (
    <Stack spacing={1}>
      {!finished && (
        <Box>
          <LinearProgress variant="determinate" value={percent} />
          <Typography variant="caption" color="text.secondary">
            {verb} {progress.sent} of {progress.total}
            {progress.failed > 0 && ` · ${progress.failed} failed`} — paced so we stay inside the
            chat service's limits, about a minute between batches of fifty.
          </Typography>
        </Box>
      )}
      {finished && (
        <Alert severity={progress.failed > 0 ? "warning" : "success"}>
          ✅ {verb} {progress.sent} of {progress.total}
          {progress.failed > 0 && ` · ❌ ${progress.failed} failed`}
          {progress.error && ` — ${progress.error}`}
        </Alert>
      )}
    </Stack>
  );
};

export default BulkSendProgressPanel;
